import React from "react";
import { displayInformation, informationDisplayLabel } from "@/components/InformationDisplay";
import type { PolymarketPrediction } from "@/lib/types";

function safeHttpsUrl(value: string | undefined) {
  if (!value || value.length > 2_048) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || !url.hostname || url.username || url.password) return null;
    return url.toString();
  } catch {
    return null;
  }
}

function formatMetric(value: number | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return "Unavailable";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
}

function formatProbability(value: number) {
  if (!Number.isFinite(value) || value < 0 || value > 1) return null;
  return `${(value * 100).toFixed(1)}%`;
}

function boundedText(value: string | undefined, limit: number) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, limit) : null;
}

function validInstant(value: string | undefined) {
  const text = boundedText(value, 64);
  if (!text || !/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(text) || !Number.isFinite(Date.parse(text))) return null;
  return text;
}

export default function PredictionContext({ prediction }: { prediction: PolymarketPrediction }) {
  const information = displayInformation(prediction.information);
  // Preserve the existing Monitor gate: legacy rows without information stay visible;
  // rows with typed state require valid, available or partial data.
  const valuesUsable = !information || (information.data.validity === "valid"
    && ["available", "partial"].includes(information.data.availability));
  const probability = valuesUsable ? formatProbability(prediction.probability) : null;
  const rulesLink = safeHttpsUrl(prediction.resolutionSourceUrl);
  const eventLink = safeHttpsUrl(prediction.sourceUrl);
  const eventTitle = boundedText(prediction.question, 220);
  const marketQuestion = boundedText(prediction.marketQuestion, 500);
  const pricedOutcome = boundedText(prediction.pricedOutcome, 120);
  const endDate = validInstant(prediction.endDate);
  const rulesText = boundedText(prediction.rulesText, 1_000);
  const metadataUpdatedAt = validInstant(prediction.marketUpdatedAt ?? prediction.updatedAt ?? prediction.observedAt);
  const hasMarketVolume = typeof prediction.marketVolume24hr === "number" && Number.isFinite(prediction.marketVolume24hr) && prediction.marketVolume24hr >= 0;
  const hasMarketLiquidity = typeof prediction.marketLiquidity === "number" && Number.isFinite(prediction.marketLiquidity) && prediction.marketLiquidity >= 0;
  const informationLabel = information && (!valuesUsable || information.delivery === "last-known-good" || information.data.timeliness !== "current")
    ? informationDisplayLabel(information)
    : null;

  return (
    <section className="prediction-context" aria-label="Prediction market context">
      <p><strong>Event:</strong> {eventTitle || "Event title unavailable."}</p>
      <p><strong>Contract question:</strong> {marketQuestion || "Contract question unavailable."}</p>
      <p>
        <strong>Venue price:</strong> {probability ?? "Unavailable"}
        {probability && (pricedOutcome ? ` · ${pricedOutcome}` : " · priced outcome label unavailable")}
      </p>
      <p>Prediction market price; not a forecast.</p>
      {informationLabel && <p><strong>Information state:</strong> {informationLabel}{!valuesUsable ? ". Numeric values are unavailable." : "."}</p>}

      <dl>
        <dt>Resolution horizon</dt>
        <dd>{endDate || "Unavailable"}</dd>
        <dt>Rules excerpt (provider-reported)</dt>
        <dd>{rulesText || "Unavailable"}</dd>
        <dt>Resolution source</dt>
        <dd>{rulesLink ? <a href={rulesLink} target="_blank" rel="noopener noreferrer">Open resolution source</a> : "Unavailable"}</dd>
        <dt>Event volume (24h, provider-reported)</dt>
        <dd>{valuesUsable ? formatMetric(prediction.volume24hr) : "Unavailable"}</dd>
        <dt>Event liquidity (provider-reported)</dt>
        <dd>{valuesUsable ? formatMetric(prediction.liquidity) : "Unavailable"}</dd>
        <dt>Selected market volume (24h, provider-reported)</dt>
        <dd>{valuesUsable && hasMarketVolume ? formatMetric(prediction.marketVolume24hr) : "Unavailable"}</dd>
        <dt>Selected market liquidity (provider-reported)</dt>
        <dd>{valuesUsable && hasMarketLiquidity ? formatMetric(prediction.marketLiquidity) : "Unavailable"}</dd>
        <dt>Market metadata updated</dt>
        <dd>
          {metadataUpdatedAt || "Unavailable"} · this time is not an independently verified trade or quote time.
        </dd>
      </dl>

      <p><strong>Financial exposure:</strong> not established from the available contract fields.</p>
      {eventLink && <a href={eventLink} target="_blank" rel="noopener noreferrer">Open event and full market details</a>}
    </section>
  );
}
